package br.com.htms.inspeccaocampo.data.funcionarios

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.platform.formatDateBr

data class Funcionario(
    val id: String,
    val nome: String,
    val cpf: String,
    val admissao: String = "",
    val matricula: String = "",
    val cargoId: String = "",
    val cargoNome: String = "",
    val setorGhe: String = "",
    val vinculo: String = "",
    val situacao: String = "",
    val nascimento: String = "",
    val rg: String = "",
    val totalDocumentos: Int = 0,
    val updatedAt: Long = 0L
) {
    val cpfFormatado: String get() = Cpf.formatar(cpf)

    /** O mesmo nome pode existir como CLT e como PJ; o vínculo diferencia nas listas. */
    val nomeComVinculo: String get() = if (vinculo.isBlank()) nome else "$nome · ${vinculo.uppercase()}"
}

data class FuncionarioDocumento(
    val id: String,
    val funcionarioId: String,
    val tipo: String,
    val titulo: String,
    val fileName: String,
    val contentType: String,
    val size: Long,
    val origem: String,
    val createdAt: Long,
    val createdBy: String
) {
    val emitidoPeloPortal: Boolean get() = origem == "EMITIDO"
    val dataRotulo: String get() = formatDateBr(createdAt)
    val tipoRotulo: String get() = DocumentoTipo.rotulo(tipo)
}

data class DocumentoResumo(
    val funcionarioId: String,
    val tipo: String,
    val origem: String,
    val createdAt: Long,
    val createdBy: String
)

/** Tipos aceitos pelo Worker (`funcionariosApi.js` → DOC_TIPOS). */
object DocumentoTipo {
    val todos: List<Pair<String, String>> = listOf(
        "OS" to "Ordem de Serviço",
        "ASO" to "ASO",
        "TREINAMENTO" to "Treinamento",
        "CERTIFICADO" to "Certificado",
        "FICHA_EPI" to "Ficha de EPI",
        "OUTRO" to "Outro"
    )

    fun rotulo(id: String): String = todos.firstOrNull { it.first == id }?.second ?: id
}

object Cpf {
    fun digitos(raw: String): String = raw.filter { it.isDigit() }.take(11)

    fun valido(raw: String): Boolean {
        val c = digitos(raw)
        if (c.length != 11 || c.all { it == c[0] }) return false
        fun dig(len: Int): Int {
            var soma = 0
            for (i in 0 until len) soma += (c[i] - '0') * (len + 1 - i)
            val r = (soma * 10) % 11
            return if (r == 10) 0 else r
        }
        return dig(9) == c[9] - '0' && dig(10) == c[10] - '0'
    }

    fun formatar(raw: String): String {
        val d = digitos(raw)
        return buildString {
            d.forEachIndexed { i, ch ->
                when (i) {
                    3, 6 -> append('.')
                    9 -> append('-')
                }
                append(ch)
            }
        }
    }
}

/** Cliente das rotas `/funcionarios` do Worker. */
object FuncionariosApi {
    suspend fun listar(): Result<List<Funcionario>> =
        AuthApi.apiJson("GET", "/funcionarios").mapCatching { text ->
            JsonMap.parse(text).array("funcionarios").orEmpty()
                .mapNotNull { (it as? JsonMap)?.let(::funcionarioFrom) }
        }

    /**
     * Cria ou atualiza pelo CPF; [matricula] null mantém a já cadastrada.
     * Com [id], atualiza exatamente esse cadastro (a mesma pessoa pode ter um CLT e um PJ com o mesmo CPF).
     */
    suspend fun salvar(
        nome: String,
        cpf: String,
        admissao: String,
        cargoId: String,
        cargoNome: String,
        setorGhe: String,
        matricula: String? = null,
        id: String? = null,
        vinculo: String? = null,
        situacao: String? = null,
        nascimento: String? = null,
        rg: String? = null
    ): Result<Funcionario> {
        val body = buildString {
            append('{')
            append("\"nome\":").append(jsonStr(nome)).append(',')
            append("\"cpf\":").append(jsonStr(Cpf.digitos(cpf))).append(',')
            append("\"admissao\":").append(jsonStr(admissao)).append(',')
            if (matricula != null) append("\"matricula\":").append(jsonStr(matricula.trim())).append(',')
            if (vinculo != null) append("\"vinculo\":").append(jsonStr(vinculo)).append(',')
            if (situacao != null) append("\"situacao\":").append(jsonStr(situacao.trim())).append(',')
            if (nascimento != null) append("\"nascimento\":").append(jsonStr(nascimento.trim())).append(',')
            if (rg != null) append("\"rg\":").append(jsonStr(rg.trim())).append(',')
            append("\"cargoId\":").append(jsonStr(cargoId)).append(',')
            append("\"cargoNome\":").append(jsonStr(cargoNome)).append(',')
            append("\"setorGhe\":").append(jsonStr(setorGhe))
            append('}')
        }
        val resposta = if (id.isNullOrBlank()) {
            AuthApi.apiJson("POST", "/funcionarios", body)
        } else {
            AuthApi.apiJson("PUT", "/funcionarios/${enc(id)}", body)
        }
        return resposta.mapCatching { text ->
            JsonMap.parse(text).obj("funcionario")?.let(::funcionarioFrom)
                ?: error("Resposta inválida do servidor")
        }
    }

    /** Todos os documentos (só metadados) para o dashboard. */
    suspend fun resumoDocumentos(): Result<List<DocumentoResumo>> =
        AuthApi.apiJson("GET", "/funcionarios/resumo-documentos").mapCatching { text ->
            JsonMap.parse(text).array("documentos").orEmpty().mapNotNull { linha ->
                val c = linha as? List<*> ?: return@mapNotNull null
                DocumentoResumo(
                    funcionarioId = c.getOrNull(0) as? String ?: return@mapNotNull null,
                    tipo = c.getOrNull(1) as? String ?: "",
                    origem = c.getOrNull(2) as? String ?: "",
                    createdAt = (c.getOrNull(3) as? Number)?.toLong() ?: 0L,
                    createdBy = c.getOrNull(4) as? String ?: ""
                )
            }
        }

    suspend fun documentos(funcionarioId: String): Result<List<FuncionarioDocumento>> =
        AuthApi.apiJson("GET", "/funcionarios/${enc(funcionarioId)}/documentos").mapCatching { text ->
            JsonMap.parse(text).array("documentos").orEmpty()
                .mapNotNull { (it as? JsonMap)?.let(::documentoFrom) }
        }

    suspend fun enviarDocumento(
        funcionarioId: String,
        bytes: ByteArray,
        tipo: String,
        titulo: String,
        fileName: String,
        emitido: Boolean
    ): Result<FuncionarioDocumento> {
        val contentType = when {
            bytes.size > 3 && bytes[0] == '%'.code.toByte() && bytes[1] == 'P'.code.toByte() -> "application/pdf"
            bytes.size > 3 && bytes[0] == 0x89.toByte() -> "image/png"
            else -> "image/jpeg"
        }
        val q = "?tipo=${enc(tipo)}&titulo=${enc(titulo)}&fileName=${enc(fileName)}" +
            if (emitido) "&origem=EMITIDO" else ""
        return AuthApi.apiUploadBytes("/funcionarios/${enc(funcionarioId)}/documentos$q", bytes, contentType)
            .mapCatching { text ->
                JsonMap.parse(text).obj("documento")?.let(::documentoFrom)
                    ?: error("Resposta inválida do servidor")
            }
    }

    suspend fun baixar(docId: String): Result<ByteArray> =
        AuthApi.apiDownloadBytes("/funcionarios/documentos/${enc(docId)}/arquivo")

    suspend fun remover(docId: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/funcionarios/documentos/${enc(docId)}", "{}").map { }

    private fun funcionarioFrom(o: JsonMap): Funcionario? {
        val id = o.str("id").ifBlank { return null }
        return Funcionario(
            id = id,
            nome = o.str("nome"),
            cpf = o.str("cpf"),
            admissao = o.str("admissao"),
            matricula = o.str("matricula"),
            cargoId = o.str("cargoId"),
            cargoNome = o.str("cargoNome"),
            setorGhe = o.str("setorGhe"),
            vinculo = o.str("vinculo"),
            situacao = o.str("situacao"),
            nascimento = o.str("nascimento"),
            rg = o.str("rg"),
            totalDocumentos = (o.long("totalDocumentos") ?: 0L).toInt(),
            updatedAt = o.long("updatedAt") ?: 0L
        )
    }

    private fun documentoFrom(o: JsonMap): FuncionarioDocumento? {
        val id = o.str("id").ifBlank { return null }
        return FuncionarioDocumento(
            id = id,
            funcionarioId = o.str("funcionarioId"),
            tipo = o.str("tipo"),
            titulo = o.str("titulo"),
            fileName = o.str("fileName"),
            contentType = o.str("contentType"),
            size = o.long("size") ?: 0L,
            origem = o.str("origem"),
            createdAt = o.long("createdAt") ?: 0L,
            createdBy = o.str("createdBy")
        )
    }

    private fun jsonStr(s: String): String = buildString {
        append('"')
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) append("\\u" + c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (c.isLetterOrDigit() && b >= 0 || c == '-' || c == '_' || c == '.' || c == '~') append(c)
            else append('%').append(((b.toInt() and 0xFF).toString(16).uppercase()).padStart(2, '0'))
        }
    }
}
